import { createContext, useContext, useEffect } from "react";
import { type ReactFlowState, useStore, useStoreApi } from "reactflow";

/** Whether viewports under it zoom from Safari's pinch gestures. Off by default, so only hosts that opt in change. */
export const SafariPinchContext = createContext(false);

export const useSafariPinch = () => useContext(SafariPinchContext);

// Safari-only; TypeScript's DOM lib has no type for it.
type GestureEvent = UIEvent & {
  scale: number;
  clientX: number;
  clientY: number;
};

type PinchTarget = {
  getZoom: () => number | undefined;
  zoomTo: (
    zoom: number,
    clientX: number,
    clientY: number,
    event: Event,
  ) => void;
};

// d3-zoom forwards a fourth argument as the gesture's sourceEvent, which its typings leave out.
type ScaleToAtPoint = (
  selection: NonNullable<ReactFlowState["d3Selection"]>,
  zoom: number,
  point: [number, number],
  event: Event,
) => void;

const isInNoWheel = (event: Event) =>
  event.target instanceof Element && Boolean(event.target.closest(".nowheel"));

/**
 * Safari sends a trackpad pinch as gesture events with only a few ctrlKey wheels, so a viewport that zooms on
 * wheels alone misses most of the pinch. This zooms from the gesture's scale, 1:1 with the fingers. Chromium sends
 * no gesture events. Returns a cleanup.
 */
export function listenForSafariPinch(
  element: HTMLElement,
  target: PinchTarget,
): () => void {
  let startZoom: number | undefined;
  const onStart = (event: Event) => {
    if (isInNoWheel(event)) return;
    startZoom = target.getZoom();
    // Taken, Safari sends no ctrlKey wheels for the rest of the pinch, so nothing zooms twice.
    if (startZoom !== undefined) event.preventDefault();
  };
  const onChange = (event: Event) => {
    if (startZoom === undefined) return;
    event.preventDefault();
    const gesture = event as GestureEvent;
    target.zoomTo(
      startZoom * gesture.scale,
      gesture.clientX,
      gesture.clientY,
      event,
    );
  };
  const onEnd = (event: Event) => {
    if (startZoom === undefined) return;
    event.preventDefault();
    startZoom = undefined;
  };
  const options = { capture: true, passive: false };
  element.addEventListener("gesturestart", onStart, options);
  element.addEventListener("gesturechange", onChange, options);
  element.addEventListener("gestureend", onEnd, options);
  return () => {
    element.removeEventListener("gesturestart", onStart, options);
    element.removeEventListener("gesturechange", onChange, options);
    element.removeEventListener("gestureend", onEnd, options);
  };
}

/** Rendered inside <ReactFlow>: a Safari pinch zooms around the fingers. */
export function SafariPinchZoom() {
  const domNode = useStore((state) => state.domNode);
  const store = useStoreApi();

  useEffect(() => {
    if (!domNode) return;
    return listenForSafariPinch(domNode, {
      getZoom: () => store.getState().transform[2],
      zoomTo: (zoom, clientX, clientY, event) => {
        const { d3Zoom, d3Selection } = store.getState();
        const pane = d3Selection?.node();
        if (!d3Zoom || !d3Selection || !pane) return;
        const rect = pane.getBoundingClientRect();
        // scaleTo keeps the flow point under the fingers fixed, clamps to the zoom range, and applies synchronously.
        (d3Zoom.scaleTo as unknown as ScaleToAtPoint)(
          d3Selection,
          zoom,
          [clientX - rect.left, clientY - rect.top],
          event,
        );
      },
    });
  }, [domNode, store]);

  return null;
}
